<template>
  <van-popup v-model="popShow" :close-on-click-overlay="false" position="bottom">
    <div ref="popupRef" class="verify_popup">
      <img :class="$store.state.isMirroring && 'mirror'" class="close" src="@/assets/close.png" @click="closePopup" />
      <div class="title">{{ title }}</div>
      <div v-if="subtitle" class="sub_title">{{ subtitle }}</div>
      <div v-if="editType === 'change' && isChangeEmail" class="sub_title">
        {{ $t('accountSecurity.newEmailVerification') }}
      </div>
      <div>
        <!-- 输入框title -->
        <div class="input_title" :class="[$store.state.isMirroring && 'ta-r']">
          <span>{{ isChangeEmail ? $t('accountSecurity.newEmail') : $t('accountSecurity.newPhoneNumber') }}</span>
          <span v-if="accountError" class="error_msg">
            {{
              isChangeEmail ? $t('loginAndRegister.emailFormatToast') : $t('loginAndRegister.phoneNumberFormatToast')
            }}
          </span>
        </div>
        <!-- 新邮箱输入 -->
        <CommonInput
          v-show="isChangeEmail"
          v-model="account"
          :placeholder="$t('common.email')"
          :on-blur="checkAccount"
          theme="white"
          show-clear
          auto-focus
        ></CommonInput>
        <!-- 新手机号输入 -->
        <div v-show="!isChangeEmail" class="input_box" :class="inputBoxFocus && 'focus'">
          +
          <router-link
            class="input_area area_code_input"
            :to="{
              name: 'Country',
              query: { redirect: 'Home' }
            }"
            replace
          >
            <span v-show="areaCode"> {{ areaCode }}</span>
          </router-link>
          <div class="split"></div>
          <input
            ref="phoneInputRef"
            class="input_area phone_input"
            :class="[$store.state.isMirroring && 'mirror']"
            v-model="account"
            type="tel"
            :placeholder="$t('common.phone')"
            inputmode="numeric"
            autocomplete="off"
            @blur="checkAccount"
            @focus="inputBoxFocus = true"
          />
          <img
            v-if="account"
            class="clear_icon"
            src="@/assets/clear_icon.png"
            alt=""
            @touchend.stop.prevent="clearPhone"
          />
        </div>
        <!-- 验证码 -->
        <div>
          <div class="input_title" :class="[$store.state.isMirroring && 'ta-r']">
            <span>{{ isChangeEmail ? $t('common.emailCode') : $t('common.smsVerificationCode') }}</span>
          </div>
          <CommonInput
            v-model="validCode"
            type="tel"
            inputmode="numeric"
            :placeholder="$t('accountSecurity.enterVerificationCode')"
            :class="[$store.state.isMirroring && 'mirror']"
            theme="white"
            :max-length="VALID_CODE_LENGTH"
          >
            <div class="send_span" :class="(msgValidating || !account) && 'disabled'" @touchend.stop.prevent="sendCode">
              {{ !msgValidating ? (firstSendMsg ? $t('common.send') : $t('common.resend')) : count + 's' }}
            </div>
          </CommonInput>
        </div>
      </div>
      <CommonBtn class="next_btn" :text="confirmBtnText" :disabled="disableConfirm" :on-click="changeAccount" />
    </div>
  </van-popup>
</template>

<script>
import CommonInput from '@/components/CommonInput'
import CommonBtn from '@/components/CommonBtn.vue'
import { Toast } from 'vant'
import { getUserInfo, getAreaCodeByIP } from '@/service'
import throttle from 'lodash/throttle'
import { isInWebView } from '@/utils'
import { VALID_CODE_LENGTH } from '@/utils/constants'
import { parseCountryFromParams, resolveCountry, syncSelectedCountryOnAreaCodeChange, flattenCountryList } from '@/utils/country'

export default {
  name: 'AccountEditPopup',
  components: {
    CommonInput,
    CommonBtn
  },
  props: {
    value: { type: Boolean, required: true, default: false },
    isChangeEmail: { type: Boolean, default: true }, // 是否为邮箱操作(绑定或修改)，否则手机账号操作(绑定或修改)
    editType: { type: String, default: 'bind' }, // 编辑类型: bind、change
    apiParams: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      VALID_CODE_LENGTH,
      popShow: false,
      validCode: '',
      phone: '',
      count: 59,
      msgValidating: false,
      firstSendMsg: true,
      title: this.$t('accountSecurity.changeEmail'),
      subtitle: '',
      account: '',
      accountError: true,
      inputBoxFocus: true,
      areaCode: '',
      countryList: [],
      selectedCountry: null
    }
  },
  watch: {
    value(val) {
      this.popShow = val
      if (val) {
        this.initTitle()
        this.initAreaCode()
        this.autoFocus()
        if (isInWebView()) {
          document.addEventListener('touchstart', this.checkInputFocus)
          this.checkInputFocus()
        }
      } else {
        if (isInWebView()) {
          document.removeEventListener('touchstart', this.checkInputFocus)
        }
      }
    },
    popShow(val) {
      this.$emit('input', val)
    },
    validCode(val) {
      if (val.length === this.VALID_CODE_LENGTH) {
        this.changeAccount()
      }
    },
    areaCode(val) {
      this.selectedCountry = syncSelectedCountryOnAreaCodeChange(this.selectedCountry, this.countryList, val)
    },
    countryList(list) {
      if (list.length && this.areaCode) {
        this.selectedCountry = syncSelectedCountryOnAreaCodeChange(this.selectedCountry, list, this.areaCode)
      }
    },
    '$route.params'() {
      this.applyCountryFromRoute()
    }
  },
  computed: {
    confirmBtnText() {
      if (this.isChangeEmail) {
        return this.$t('accountSecurity.change')
      } else {
        return this.editType === 'bind' ? this.$t('common.bind') : this.$t('common.done')
      }
    },
    disableConfirm() {
      this.checkAccount()
      if (this.isChangeEmail) {
        return !this.account || this.accountError
      } else {
        return !this.account || !this.areaCode || this.accountError || !this.country
      }
    },
    country() {
      return resolveCountry({
        selectedCountry: this.selectedCountry,
        countryList: this.countryList,
        areaCode: this.areaCode
      })
    }
  },
  created() {
    this.getCountryList()
    this.initAreaCode()
  },
  methods: {
    applyCountryFromRoute() {
      const country = parseCountryFromParams(this.$route.params)
      if (country) {
        this.selectedCountry = country
        this.areaCode = country.areaCode
      }
    },
    getAreaCodeByIP,
    checkInputFocus() {
      setTimeout(() => {
        if (document.activeElement.tagName === 'INPUT') {
          this.$refs.popupRef.style.paddingBottom = '35vh'
        } else {
          this.$refs.popupRef.style.paddingBottom = '0.24rem'
        }
      }, 200)
    },
    resetData() {
      this.validCode = ''
      this.count = 59
      this.msgValidating = false
      this.firstSendMsg = true
      this.accountError = false
      this.inputBoxFocus = false
      this.account = ''
    },
    async initAreaCode() {
      if (localStorage.getItem('paramsData')) {
        let data = JSON.parse(localStorage.getItem('paramsData'))
        if (this.$route.query.loginType === data.registerType || this.$route.params.loginType === data.registerType) {
          // localStorage中的登录方式和路由中的相同
          this.account = data.account
          // 优先取路由params里的国家信息，解决修改国家失败的问题
          this.areaCode = this.$route.params.areaCode || data.areaCode
          this.loginType = data.registerType
          if (!this.$route.params.country && !this.$route.params.countryNo && data.countryNo) {
            this.selectedCountry = parseCountryFromParams(data)
          }
        } else {
          this.loginType = this.$route.query.loginType || this.$route.params.loginType
          this.areaCode = this.$route.params.areaCode
        }
      } else {
        this.loginType = this.$route.query.loginType || this.$route.params.loginType
        this.areaCode = this.$route.params.areaCode
      }
      this.applyCountryFromRoute()
      if (!this.isChangeEmail && !this.areaCode) {
        this.areaCode = await this.getAreaCodeByIP()
      }
    },
    getCountryList() {
      this.$http.get(window.serverPath + '/api/country/getNewAreaCodeList').then((res) => {
        if (res.data.code === '200') {
          this.countryList = flattenCountryList(res.data.data)
        }
      })
    },
    initTitle() {
      if (this.editType === 'bind') {
        if (this.isChangeEmail) {
          this.title = this.$t('accountSecurity.bindEmail')
          this.subtitle = this.$t('accountSecurity.bindEmailNote')
        } else {
          this.title = this.$t('accountSecurity.bindPhone')
          this.subtitle = this.$t('accountSecurity.smsBindNote')
        }
      } else {
        if (this.isChangeEmail) {
          this.title = this.$t('accountSecurity.changeEmail')
          this.subtitle = this.$t('accountSecurity.changeNote')
        } else {
          this.title = this.$t('accountSecurity.changePhone')
          this.subtitle = this.$t('accountSecurity.changeNote')
        }
      }
    },
    handleFocus() {
      this.inputBoxFocus = true
    },
    autoFocus() {
      if (!this.isChangeEmail) {
        this.$nextTick(() => {
          this.$refs.phoneInputRef.focus()
        })
      }
    },
    clearPhone() {
      this.account = ''
    },
    checkAccount() {
      this.inputBoxFocus = false
      if (!this.account) {
        this.accountError = false
        return
      }
      if (this.isChangeEmail) {
        const regEmail = /^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/
        this.accountError = !regEmail.test(this.account)
      } else {
        const regPhone = /^[0-9]{7,11}$/
        this.accountError = !regPhone.test(this.account)
      }
    },
    async sendCode() {
      try {
        if (this.msgValidating) {
          return
        }
        // 校验邮箱是否已存在前置
        if (this.isChangeEmail && !(await this.checkEmail())) return
        let url = ''
        let params = {}

        if (this.isChangeEmail) {
          url = window.serverPath + '/api/auth/getNewEmailCode/CHANGE_BINDING_ACCOUNT'
          params = { email: this.account.toLowerCase() }
        } else {
          url = window.serverPath + '/api/auth/getNewSmsCode/BINDING_ACCOUNT'
          const areaCode = this.areaCode
          params = {
            phone: areaCode + '' + this.account,
            userId: this.$store.state.userInfo.userId,
            areaCode
          }
        }

        const { data } = await this.$http.post(url, params)

        if (data.code === '200') {
          this.msgValidating = true
          this.firstSendMsg = false
          this.showSendSuccessToast(data.data === 'WhatsApp')
          const interval = window.setInterval(() => {
            this.count -= 1
            if (this.count <= 0) {
              clearInterval(interval)
              this.msgValidating = false
              this.count = 59
            }
          }, 1000)
        } else if (data.code === '60001' && this.isChangeEmail) {
          Toast({
            message: this.$t('loginAndRegister.userExistToast')
          })
          this.msgValidating = false
          this.count = 59
        } else {
          Toast({
            message: data.message
          })
          this.msgValidating = false
          this.count = 59
        }
      } catch (err) {
        console.log('异常')
        this.msgValidating = false
        this.count = 59
      }
    },
    changeAccount: throttle(
      function () {
        if (this.isChangeEmail) {
          this.changeEmail()
        } else {
          this.changePhone()
        }
      },
      5000,
      { trailing: false }
    ),
    // 提前校验邮箱是否存在
    async checkEmail() {
      try {
        const mail = this.account.toLowerCase()
        const url = window.serverPath + '/api/auth/email/check'
        const { data } = await this.$http.post(url, { email: mail })
        if (data.code === '200') {
          return true
        }
        if (data.code === '60001') {
          Toast({
            message: this.$t('loginAndRegister.userExistToast')
          })
          return false
        } else {
          Toast({
            message: data.message
          })
        }
        return false
      } catch (err) {
        console.error('异常:', err)
        return false
      }
    },
    showSendSuccessToast(toWhatsapp) {
      const msg = this.isChangeEmail
        ? this.$t('redesign.sendToEmail')
        : toWhatsapp
          ? this.$t('redesign.sendToWhatsapp')
          : this.$t('redesign.sendToPhone')
      Toast({
        message: msg
      })
    },
    changeEmail() {
      let paramsData = {}
      paramsData.email = this.account.toLowerCase()
      paramsData.validCode = this.validCode
      paramsData.oldEmail = this.apiParams.oldEmail || ''
      paramsData.oldPhone = this.apiParams.oldPhone || ''
      paramsData.code = this.apiParams.code
      if (this.apiParams.areaCode) {
        paramsData.areaCode = this.apiParams.areaCode
      }
      const successMsg =
        this.editType === 'bind' ? this.$t('paymentCode.successToast') : this.$t('accountSecurity.changeSuccessToast')
      this.$http
        .post(window.serverPath + '/api/auth/checkEmailCode/CHANGE_BINDING_ACCOUNT', paramsData)
        .then((res) => {
          if (res.data.code === '200') {
            this.$store.commit('setSecurityToken', res.data.data.securityToken)
            const $this = this
            const params = { type: 'EMAIL' }
            params.securityToken = res.data.data.securityToken
            params.email = this.account.toLowerCase()
            params.oldEmail = this.apiParams.oldEmail || ''
            params.oldPhone = this.apiParams.oldPhone || ''
            params.code = this.apiParams.code
            if (this.apiParams.areaCode) {
              params.areaCode = this.apiParams.areaCode
            }
            $this.$http
              .post(window.serverPath + '/api/user/editAccount/EMAIL', params)
              .then((res) => {
                if (res.data.code === '200') {
                  this.$store.commit('delSecurityToken')
                  this.getUserInfo()
                  Toast.success({
                    message: successMsg,
                    onClose: () => {
                      // 关闭弹窗
                      this.closePopup()
                    }
                  })
                } else if (res.data.code === '60011') {
                  Toast(res.data.message)
                } else {
                  Toast({
                    message: res.data.message
                  })
                }
              })
              .catch((err) => {
                console.log('异常', err)
              })
          } else {
            Toast({
              message: this.$t('accountSecurity.wrongCodeToast')
            })
          }
        })
        .catch((_err) => {
          console.log('异常')
        })
    },
    changePhone() {
      const params = { type: 'PHONE' }
      const areaCode = this.areaCode
      params.countryNo = this.country.countryNo
      params.phone = areaCode + '' + this.account
      let paramsData = {}
      paramsData.phone = areaCode + '' + this.account.replace(/\D/g, '')
      paramsData.validCode = this.validCode
      if (this.editType === 'change') {
        paramsData.oldEmail = this.apiParams.oldEmail || ''
        paramsData.oldPhone = this.apiParams.oldPhone || ''
        paramsData.code = this.apiParams.code
        params.oldEmail = this.apiParams.oldEmail
        params.oldPhone = this.apiParams.oldPhone
        params.code = this.apiParams.code
      }
      this.$http
        .post(window.serverPath + '/api/auth/checkSmsCode/BINDING_ACCOUNT', paramsData)
        .then((res) => {
          if (res.data.code === '200') {
            this.$store.commit('setSecurityToken', res.data.data.securityToken)
            if (params.phone) {
              if (this.account.startsWith(areaCode)) {
                Dialog.confirm({
                  title: this.$t('common.attention'),
                  message: this.$t('loginAndRegister.countryCodeRepeatToast'),
                  cancelButtonText: this.$t('common.modify'),
                  confirmButtonText: this.$t('common.ignore')
                })
                  .then(() => {
                    this.confirm(params)
                  })
                  .catch(() => {})
              } else {
                this.confirm(params)
              }
            } else {
              Toast({
                message: this.$t('loginAndRegister.phoneIncorrectToast')
              })
            }
          } else {
            Toast({
              message: this.$t('accountSecurity.wrongCodeToast')
            })
          }
        })
        .catch((_err) => {
          console.log('异常')
        })
    },
    confirm(params) {
      params.securityToken = this.$store.state.securityToken
      const successMsg =
        this.editType === 'bind' ? this.$t('paymentCode.successToast') : this.$t('accountSecurity.changeSuccessToast')
      this.$http
        .post(window.serverPath + '/api/user/editAccount/PHONE', params)
        .then((res2) => {
          if (res2.data.code === '200') {
            Toast.success({
              message: successMsg,
              onClose: () => {
                // 关闭弹窗
                this.closePopup()
              }
            })
            this.$store.commit('delSecurityToken')
            this.$store.commit('delUserInfo')
            this.getUserInfo()
          } else {
            Toast({
              message: this.$t('accountSecurity.verificationWrongToast')
            })
          }
        })
        .catch((err) => {
          console.log('异常', err)
        })
    },
    getUserInfo,
    // 关闭并清空状态
    closePopup() {
      this.popShow = false
      this.resetData()
      this.$emit('closePopup')
    }
  }
}
</script>

<style scoped>
.verify_popup {
  /* height: 4rem; */
  padding: 0.24rem;
  background: #fff;
  border-radius: 0.16rem 0.16rem 0 0;
  position: relative;
}

.close {
  position: absolute;
  top: 0.24rem;
  right: 0.24rem;
  width: 0.24rem;
  height: 0.24rem;
}

.close.mirror {
  left: 0.24rem;
  right: auto;
}

.title {
  display: flex;
  color: #2f2f2f;
  font-family: 'PingFang SC';
  font-size: 0.2rem;
  font-style: normal;
  font-weight: 600;
  line-height: 0.28rem;
  margin-bottom: 0.04rem;
  max-width: calc(100% - 0.4rem);
  text-align: start;
}

.sub_title,
.account {
  display: flex;
  color: #3fbb70;
  font-family: 'PingFang SC';
  font-size: 0.14rem;
  font-style: normal;
  font-weight: 600;
  line-height: 0.22rem;
}

.input_title {
  text-align: start;
  margin-top: 0.2rem;
  padding: 0.02rem 0.16rem;
  color: #2f2f2f;
  display: flex;
  align-items: center;
  font-family: 'PingFang SC';
  font-size: 0.12rem;
  font-style: normal;
  font-weight: 600;
  line-height: 0.2rem;
  gap: 0.08rem;
}

.input_title .error_msg {
  color: #ff4c62;
  font-size: 0.1rem;
}

.text_span {
  color: #3fbb70;
  font-size: 0.14rem;
  font-family: 'PingFang SC';
  font-style: normal;
  font-weight: 600;
  line-height: 0.22rem;
  white-space: nowrap;
}

.send_span {
  color: #3fbb70;
  font-size: 0.14rem;
  font-family: 'PingFang SC';
  font-style: normal;
  font-weight: 400;
  line-height: 0.22rem;
  white-space: nowrap;
}

.send_span.disabled {
  color: #828282;
}

.next_btn {
  margin: 0.48rem auto 0;
  margin-bottom: 0.12rem;
  font-size: 0.16rem !important;
}

.input_box {
  width: 100%;
  padding: 0.1rem 0.16rem;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-radius: 0.08rem;
  height: 0.48rem;
  border: 0.01rem solid #d6d6d6;
  color: #2f2f2f;
  font-size: 0.14rem;
  line-height: 0.14rem;
}

.input_box.focus {
  border: 0.01rem solid #3fbb70;
}

.split {
  width: 0.01rem;
  height: 0.18rem;
  margin: 0 0.12rem;
  background: #2f2f2f;
}

.input_area {
  color: #2f2f2f;
  font-size: 0.14rem;
  line-height: 0.14rem;
  text-align: left;
  height: 0.48rem;
  background: transparent;
  caret-color: #3fbb70;
  font-weight: 600;
}

.input_area.area_code_input {
  line-height: 0.48rem;
  min-width: 0.42rem;
  margin-inline-start: 0.03rem;
}

.input_area::placeholder {
  color: #828282;
  font-weight: 400;
}

.input_area.phone_input {
  flex: 1;
}

.input_area.mirror {
  text-align: right;
}

.clear_icon {
  width: 0.2rem;
  height: 0.2rem;
}

.input_inner_area_code {
  width: 0.5rem;
  display: inline-block;
  font-size: 0.14rem;
  color: #282828;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components/Account/EditPopup.vue